import { type VariantProps } from 'class-variance-authority'
import { cn } from '@/shared/lib/utils'
import { buttonVariants } from '@/shared/components/ui/button'

export type AppBtnVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'soft' | 'dangerSolid'
export type AppBtnTone = 'sky' | 'indigo' | 'emerald' | 'amber' | 'rose'
export type AppBtnSize = 'xs' | 'sm' | 'md'

type ShadcnBtnVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>
type ShadcnBtnSize = NonNullable<VariantProps<typeof buttonVariants>['size']>

const SOFT_TONE: Record<AppBtnTone, string> = {
  sky: 'border-sky-500/30 bg-sky-500/10 text-sky-200 hover:border-sky-400/45 hover:bg-sky-500/14 hover:text-sky-100',
  indigo: '',
  emerald:
    'border-emerald-500/30 bg-emerald-500/10 text-emerald-200 hover:border-emerald-400/45 hover:bg-emerald-500/14 hover:text-emerald-100',
  amber:
    'border-amber-500/30 bg-amber-500/10 text-amber-200 hover:border-amber-400/45 hover:bg-amber-500/14 hover:text-amber-100',
  rose: 'border-rose-500/30 bg-rose-500/10 text-rose-200 hover:border-rose-400/45 hover:bg-rose-500/14 hover:text-rose-100',
}

function mapVariant(variant: AppBtnVariant, tone: AppBtnTone = 'amber'): ShadcnBtnVariant {
  switch (variant) {
    case 'primary':
      return tone === 'indigo' ? 'indigo' : 'default'
    case 'secondary':
      return 'outline'
    case 'ghost':
      return 'ghost'
    case 'danger':
      return 'destructive'
    case 'soft':
      return 'accent'
    case 'dangerSolid':
      return 'destructiveSolid'
    default:
      return 'default'
  }
}

function mapSize(size: AppBtnSize): ShadcnBtnSize {
  switch (size) {
    case 'xs':
      return 'xs'
    case 'sm':
      return 'sm'
    case 'md':
    default:
      return 'default'
  }
}

/** 映射为 shadcn Button props */
export function protoBtnProps({
  variant = 'primary',
  size = 'md',
  tone = 'amber',
  className,
}: {
  variant?: AppBtnVariant
  size?: AppBtnSize
  tone?: AppBtnTone
  className?: string
}) {
  return {
    variant: mapVariant(variant, tone),
    size: mapSize(size),
    className: cn(variant === 'soft' && tone !== 'indigo' ? SOFT_TONE[tone] : null, className),
  } as const
}

/** 全站统一按钮 class（Link 等可复用） */
export function appBtnClassName({
  variant = 'primary',
  size = 'md',
  tone = 'amber',
  className,
}: {
  variant?: AppBtnVariant
  size?: AppBtnSize
  tone?: AppBtnTone
  className?: string
}) {
  return cn(
    buttonVariants({ variant: mapVariant(variant, tone), size: mapSize(size) }),
    variant === 'soft' && tone !== 'indigo' ? SOFT_TONE[tone] : null,
    className,
  )
}

export function appIconBtnClassName({
  tone = 'default',
  className,
}: {
  tone?: 'default' | 'danger'
  className?: string
} = {}) {
  return cn(
    buttonVariants({ variant: 'ghost', size: 'icon-sm' }),
    tone === 'danger' && 'text-muted-foreground hover:text-destructive hover:bg-destructive/10',
    className,
  )
}

export { Button as AppButton } from '@/shared/components/ui/button'
